/**
 * The app shell — the chrome every page inside `app.` renders in.
 *
 * One component, used by every route on that host, so a page never draws its
 * own header or footer and cannot drift from the others.
 *
 * **It is NOT the public site's chrome.** That one (`PublicChrome`) carries
 * marketing navigation — Features, Showcase, Festivals, Download, About — and
 * a Log in / Register button. None of that belongs here: everybody looking at
 * this is signed in, and a "Register" button in front of somebody with an
 * account is furniture in the way.
 *
 * So the header is: the brand on the start edge, and on the end edge the
 * person's own identity and a menu. Nothing else competes.
 */
import { syncArrivals } from '@/lib/arrival';
import { Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import { accountUrl, appUrl, consoleUrl, festivalUrl, isAccountHost, isAppHost, isConsoleHost, isRaveAdminHost, isSupportHost, legalUrl, raveAdminUrl, supportUrl } from '@/lib/tenant';
import { BrandLockup, LogoMark, Row, Stack, Wordmark, Wrap, cx } from '@/ui';
import { AppHeader } from '@/ui/chrome';
import { useAuth, leaveTo } from './AuthProvider';
import { NotificationOpened } from './NotificationOpened';
import { VersionMatrixButton } from './VersionMatrix';
import { useTenant } from './TenantProvider';
import { initialsFor } from '@/visitor/initials';
import { readJson, writeJson } from '@/lib/storage';
import { MaintenanceBanner } from './platform';
import { LanguageSwitch } from './LanguageSwitch';
import { useT } from '@/i18n';
import { useLocale } from './LocaleProvider';
import { planChip, planName, useEntitlements } from '@/lib/entitlements';
import { HeaderSearch } from './HeaderSearch';
import { isFramed } from '@/lib/framed';
import { inPreview } from '@/lib/preview';
import { PullToRefresh, useHeaderAway } from './scrollChrome';
import { HeaderInbox, InboxMenuItems } from './HeaderInbox';
import { useCurrentRave } from '@/lib/currentRave';
import { NativePushAuto } from './NativePushAuto';
import { StoreSync } from './StoreSync';
import './AppShell.css';
import { FollowUs, SocialIcons } from './SocialIcons';
import { usePurchasesOn } from '@/lib/catalogue';
import { askToSignIn } from '@/lib/signInFirst';
import { inNativeApp } from '@/lib/nativeBridge';
import { comingRaves } from '@/lib/myRaveCounts';
import { RaveMenuName } from './RaveMenuName';
import { SignInStrip } from './SignInStrip';
import { SignInAsk } from './SignInAsk';
import { myRavesPath } from './AppLaunch';
import { PeopleCount } from './PeopleCount';
import { useInviteFriend } from './InviteFriend';

/**
 * The main menu's icons — ONE table for the user console's sidebar (`UserShell`) and the main
 * rows of a rave's menu (`ShellDrawer`), so the same row never wears two icons (the user,
 * 2026-10-29: "the main menu in the bottom don't have the right icons"). My raves is the heart
 * and Upcoming raves the MyRave logo — the bottom bar's.
 */
export const MAIN_MENU_ICONS = {
  heart: 'M12 21s-8-5.1-8-10.3A4.7 4.7 0 0 1 12 7a4.7 4.7 0 0 1 8 3.7C20 15.9 12 21 12 21z',
  find: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM16 8l-2 6-6 2 2-6z',
  // Headphones — every artist on MyRave (step 76).
  artists: 'M3 18v-6a9 9 0 0 1 18 0v6M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z',
  plans: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7l1.5 3 3.3.5-2.4 2.3.6 3.3-3-1.6-3 1.6.6-3.3-2.4-2.3 3.3-.5z',
  // A question in a circle — Support (2026-10-29).
  support: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01',
  // A ticket — My tickets, the bottom bar's Tickets (the bar's own drawing, `FestivalModeBar`).
  tickets: 'M3 9a3 3 0 0 0 0 6v4h18v-4a3 3 0 0 1 0-6V5H3zM13 5v2M13 11v2M13 17v2',
  // A person with a plus — Invite a friend (step 89).
  invite: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M19 8v6M22 11h-6',
  chevron: 'M6 9l6 6 6-6',
} as const;

/**
 * What to call the person.
 *
 * `displayName` if they gave one, otherwise the local part of their email —
 * never the whole address. A header is on screen in public, and somebody's
 * email should not be readable over their shoulder at a festival. An account
 * made from the command line has neither, which is why there is a third
 * fallback rather than an empty space.
 */
function nameFor(user: { displayName: string | null; email: string | null }, fallback: string): string {
  if (user.displayName) return user.displayName;
  if (user.email) return user.email.split('@')[0] || fallback;
  return fallback;
}

const icon = (d: string) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);

/* The header search lives in `HeaderSearch.tsx`: live results, keyboard and
   all, which is more than a shell file should carry inline. */

/**
 * A row in the app menu. Exported so a rave's own sections join the SAME
 * drawer rather than opening a second one beside it.
 */
/** The header's computer layout (the search open in its middle) — the shell's own breakpoint. */
const WIDE_HEADER = '(min-width: 880px)';
function useWideHeader(): boolean {
  return useSyncExternalStore(
    (on) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const m = window.matchMedia(WIDE_HEADER);
      m.addEventListener('change', on);
      return () => m.removeEventListener('change', on);
    },
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(WIDE_HEADER).matches,
    () => false,
  );
}

export function MenuItem({ onClick, children, danger, current, chevron }: {
  onClick: () => void; children: ReactNode; danger?: boolean; current?: boolean;
  /** Marks an item that opens another level rather than going somewhere. */
  chevron?: boolean;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      /*
       * `aria-current="page"` as well as the tint. Where you are is not
       * something a screen reader can infer from a background colour, and this
       * is the one item in the list somebody needs to be able to skip.
       */
      aria-current={current ? 'page' : undefined}
      className={danger ? 'shell-item is-danger' : 'shell-item'}
    >
      {children}
      {chevron && (
        <span aria-hidden="true" className="shell-chevron">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
        </span>
      )}
    </button>
  );
}

/**
 * The person's bubble, their name, and the menu that opens under it.
 *
 * The bubble is a photo when the person signed in with a provider that gave us
 * one, and their initials otherwise. It was initials only while the account
 * model had no avatar at all; `avatarUrl` arrived 2026-09-25.
 *
 * **Initials remain the default, not the fallback of last resort.** A password
 * account has no picture and never will until uploads exist, so the gradient
 * bubble is what most people see and it has to be the designed state rather
 * than an empty frame.
 *
 * Closes on Escape and on a click anywhere outside, both of which people
 * expect of a menu and neither of which a browser gives for free.
 */
/** "Invite a friend" (step 89): the QR code and the ways to share it — a row in every menu. */
function InviteFriendRow({ onPick }: { onPick?: () => void }) {
  const tr = useT();
  const invite = useInviteFriend();
  if (!invite) return null;
  return (
    <MenuItem onClick={() => { onPick?.(); invite.open(); }}>
      {icon(MAIN_MENU_ICONS.invite)} {tr('Invite a friend')}
    </MenuItem>
  );
}

export function UserMenu({ onOpenAccount }: { onOpenAccount: () => void }) {
  const tr = useT();
  /*
   * Asked for once and cached: it changes when somebody pays, which is not
   * often, and this menu is opened on every screen.
   */
  const managed = useQuery({
    queryKey: ['me', 'managed-raves'],
    queryFn: () => api.get<{ raves: { editionId: string; subdomain: string; editionSlug: string }[] }>('/me/managed-raves'),
    staleTime: 10 * 60_000,
    retry: false,
  });
  // The rave on screen, when this person administers it: "Manage O.Z.O.R.A 2222"
  // (the user, 2026-10-08 — it replaced the header's gear).
  const current = useCurrentRave();
  const managesThis = current ? managed.data?.raves?.find((r) => r.editionId === current.editionId) : undefined;

  /*
   * What the account holds — the payment model's `/me/entitlements` (step 24);
   * the legacy `/me/subscription` it replaced opens nothing any more.
   */
  const plan = useEntitlements();
  const { user, signOut, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    // `mousedown`, not `click`: a click fires after the button's own handler
    // has already toggled it, so the menu would close and reopen in one press.
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  /*
   * Signed out, the menu's place is the way in (the user, 2026-10-26: "users will be able
   * to login via the user profile menu in the header as well") — back to this page after.
   */
  if (!user) {
    // The phone app signs in from the strip under the header, not the header (the user,
    // 2026-10-26: "in the rave app use the same sign in header and not in the master header").
    if (loading || isFramed() || inPreview() || inNativeApp()) return null;
    return (
      <div className="shell-me-wrap">
        <button type="button" className="shell-me shell-sign-in" onClick={() => askToSignIn()}>
          <span aria-hidden="true" className="shell-avatar">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
            </svg>
          </span>
          {/* The whole offer where there is room (the user, 2026-10-27: "in the header say sign in to
              gain full access"); "Sign in" on a narrow screen (AppShell.css). */}
          <span className="shell-sign-in-label is-long">{tr('Sign in to gain full access')}</span>
          <span className="shell-sign-in-label is-short">{tr('Sign in')}</span>
        </button>
      </div>
    );
  }
  const name = nameFor(user, tr('Account'));

  return (
    <div ref={wrap} className="shell-me-wrap">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={tr('{name} — account menu', { name })}
        data-open={open || undefined}
        className="shell-me"
        
      >


        {/*
          * **The avatar LEADS, so the name follows it in the inline flow.**
          *
          * That is what puts the name to the RIGHT of the avatar in English
          * and to its LEFT in Hebrew, with no branch and no direction check:
          * "after" is a logical position, and every offset below is a logical
          * property, so the whole control mirrors with the document.
          *
          * The control is the last thing in the header row, so growing widens
          * it toward the start — the avatar travels left as the name is
          * uncovered and the search beside it is pushed along. The avatar
          * moving is the point; it is what the name appears out of.
          */}
        <span
          aria-hidden="true"
          className="shell-avatar"
        >
          {user.avatarUrl
            ? (
              <img
                src={user.avatarUrl}
                alt=""
                width={38}
                height={38}
                /*
                 * `no-referrer` is load-bearing. Google's avatar CDN answers
                 * 403 to a request carrying a referrer it does not recognise,
                 * so without this the header shows a broken image on every
                 * host but theirs.
                 */
                referrerPolicy="no-referrer"
                /*
                 * A picture that fails to load must fall back to the initials
                 * rather than leave a hole: these URLs are somebody else's and
                 * can rot, 403 or go missing without warning.
                 */
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                className="shell-avatar-img"
              />
            )
            : initialsFor(user.displayName ?? user.email ?? '?')}
        </span>

        {/*
          * **Closed, this control is the avatar and nothing else.** The name
          * and the chevron appear only while the menu is open, at the user's
          * instruction (2026-09-25).
          *
          * The header's end is where every screen puts its own controls, so a
          * name sitting there permanently spends the width they compete for on
          * something the person already knows. Opening the menu is when it is
          * worth confirming WHOSE account is about to be acted on — and
          * because this sits at the end of a flex row, the name appearing
          * widens the control and pushes what is beside it toward the start,
          * which is the movement that makes the menu feel attached to the
          * avatar rather than dropped under the header.
          *
          * The accessible name is unaffected: `aria-label` on the button names
          * the person whether or not the text is drawn, so a screen reader
          * never loses it.
          *
          * `shell-username` still hides below 560px. A long display name plus
          * a menu button plus the lockup does not fit a 390px bar, and step 51
          * is the record of what this header does when something in it is
          * allowed to wrap.
          */}
        {/*
          * **Always mounted, animated by WIDTH.** Unmounting it made the name
          * appear and vanish instantly; there is no transition between a node
          * and no node.
          *
          * `max-width` rather than `width`, because the natural width is
          * `auto` and `auto` is not an animatable value — a transition to it
          * simply jumps.
          *
          * **Width ONLY — no opacity.** Fading it in and out dissolved the
          * name in place, which reads as the text arriving separately from the
          * control. Animating the width alone makes `overflow: hidden` a
          * moving edge, so the name is uncovered by the avatar and swept back
          * under it: one object changing size rather than two things happening
          * at once.
          *
          * That `overflow: hidden` is load-bearing while collapsed, or a long
          * name spills across the header at zero width.
          */}
        <span
          className="shell-username shell-me-name"
          aria-hidden={!open}
          
        >
          {name}
        </span>

        <span
          aria-hidden="true"
          className="shell-me-caret"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
        </span>



      </button>

      {/*
        * **Always mounted, wiped open by height.** Unmounting it gave no
        * closing animation at all — there is nothing to transition out of
        * when the node has already gone.
        *
        * `max-height` for the same reason the name beside it uses `max-width`:
        * the natural height is `auto`, which cannot be animated to. With
        * `overflow: hidden` the bottom edge becomes a moving line, so the menu
        * is drawn down out of the avatar and pulled back up into it.
        *
        * **`visibility` is what makes "closed" really closed**, and its
        * transition is delayed by the length of the wipe — without the delay
        * it vanishes on the first frame and the collapse plays behind nothing.
        * `pointer-events` and `aria-hidden` go with it, or a collapsed menu
        * stays clickable and stays in the screen-reader's tree.
        *
        * Padding moved to an inner wrapper: on the outer it kept the box 12px
        * tall at `max-height: 0`, which is a sliver of border left on screen.
        */}
      <div
        role="menu"
        aria-label={tr('Account')}
        aria-hidden={!open}
        data-open={open || undefined}
        className="shell-me-menu"
      >
        <div className="shell-me-pad">
          {/* The address only when there IS one. It used to fall back to
              "No email on this account", which announces an absence nobody
              asked about — a `pnpm user:create` admin has no address by
              design, and being told so on every menu open is noise. */}
          <div className="shell-me-head">
            {/*
              * The name is NOT repeated here (removed 2026-09-25).
              *
              * It is drawn beside the avatar the moment this menu opens, three
              * pixels above — so printing it again as the first line said the
              * same thing twice in one glance and pushed everything actionable
              * further down.
              */}
            {/*
              * The PLAN under the name, not the email.
              *
              * The email is already on the account page and it is the one
              * thing somebody opening this menu certainly knows. What they
              * cannot see anywhere else is what they are subscribed to — and
              * "Free" is a real answer, not a blank: MyRave costs nothing and
              * a plan only matters for raves that charge.
              */}
            <div className="shell-me-plan">
              {plan.isLoading ? '…' : planName(plan.data, tr)}
            </div>
          </div>

          {/* Only what belongs to the ACCOUNT. "Festivals" is navigation and
              lives in the menu beside this one; having it in both made the two
              menus look like one menu split in half for no reason. */}
          {/*
            * One row, and it goes to a PAGE.
            *
            * "Change password" used to sit beside it opening its own panel,
            * which made the account two half-places: a sheet with your name
            * in it and a modal with a form in it, and nowhere that held the
            * devices you are signed in on or the button that closes the
            * account — both of which the API had all along.
            */}
          {/* Grouped, a line between each group (the user, 2026-10-29: "re-organize the drop down of
              the user profile menu — and with separators"): the account and its inbox · Invite a
              friend · managing (admins only) · the language · Sign out. The head draws its own line
              under the plan (`.shell-me-head`) — no rule after it, or two lines (2026-10-29). */}
          <MenuItem onClick={() => { setOpen(false); onOpenAccount(); }}>
            {icon('M12 3l7 3v6c0 4.4-3 8.2-7 9-4-.8-7-4.6-7-9V6z')} {tr('Your account')}
          </MenuItem>
          {/* Messages and notifications, with what is waiting — the header's
              icons again, and their only home on a narrow phone with none. */}
          <InboxMenuItems row={({ label, path, count, onClick }) => (
            <MenuItem key={label} onClick={() => { setOpen(false); onClick(); }}>
              {icon(path)} {label}
              {count > 0 && <span className="shell-menu-count">{count > 99 ? '99+' : count}</span>}
            </MenuItem>
          )} />

          <div className="shell-rule is-tight" />
          <InviteFriendRow onPick={() => setOpen(false)} />
          {/*
            * Only for somebody who actually administers a rave.
            *
            * The query answers with an empty list for everybody else, and an
            * empty list hides the row — so a visitor never learns the section
            * exists, and an organiser never has to be told where it is.
            */}
          {/*
            * `?.raves?.` — the second guard is not decoration.
            *
            * `?? 0` made this look total, but `?.` only covered `data`: a
            * response present and shaped differently threw on `.length`. It
            * never showed while the menu was unmounted when closed, because
            * the line only ran once somebody opened it. Keeping the menu
            * mounted for its animation made it run on every render and it took
            * the whole shell down — header, footer and all.
            *
            * That shape disagreement is the documented hazard of a SHARED
            * query key (`frontend/CLAUDE.md`): `['me','managed-raves']` has
            * more than one caller, and whichever ran first decides what is in
            * the cache.
            */}
          {(managesThis || (managed.data?.raves?.length ?? 0) > 0 || user?.role === 'SUPER_ADMIN') && (
            <div className="shell-rule is-tight" />
          )}
          {managesThis && current && (
            <MenuItem onClick={() => { setOpen(false); void leaveTo(raveAdminUrl(`/${managesThis.subdomain}/${managesThis.editionSlug}`)); }}>
              {icon('M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z')} {tr('Manage {name}', { name: current.name })}
            </MenuItem>
          )}
          {(managed.data?.raves?.length ?? 0) > 0 && (
            <MenuItem onClick={() => { setOpen(false); void leaveTo(raveAdminUrl('/')); }}>
              {icon('M3 21h18M5 21V8l7-5 7 5v13M10 21v-6h4v6')} {tr('Manage my raves')}
            </MenuItem>
          )}

          {/*
            * The platform console, for the one role above `USER`.
            *
            * `SUPER_ADMIN` is the whole gate, and this row is the only thing
            * on the platform that names the host — a person who is not an
            * admin never learns it exists, and an admin never has to be told
            * where it is. Hiding it is not the security; the API's check on
            * every route behind it is (`plan.md` §66.4).
            */}
          {user?.role === 'SUPER_ADMIN' && (
            <MenuItem onClick={() => { setOpen(false); void leaveTo(consoleUrl('/')); }}>
              {icon('M12 3l8 4v5c0 5-3.4 8.4-8 9.5C7.4 20.4 4 17 4 12V7z')} {tr('Admin console')}
            </MenuItem>
          )}

          {/* The language, one tap away (the user, 2026-10-28: "a quick language change under the
              user profile menu, so users can switch languages easily") — the same switch as the
              account's settings: saved on the account and the device, the region kept. */}
          <div className="shell-rule is-tight" />
          <div className="shell-me-lang"><LanguageSwitch /></div>

          <div className="shell-rule is-tight" />

          <MenuItem danger onClick={() => { setOpen(false); void signOut(); }}>
            {icon('M15 17l5-5-5-5M20 12H9M13 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h7')} {tr('Sign out')}
          </MenuItem>
        </div>
      </div>
    </div>
  );
}

/**
 * The app menu: a full drawer that opens from the START edge, with its toggle
 * on that same edge.
 *
 * The control and its result are on the same side — the hamburger is the first
 * thing in the bar under `dir="ltr"` and the last under `dir="rtl"`, and the
 * drawer slides in from whichever edge that is. A button on one edge opening a
 * panel on the other reads as two unrelated things happening.
 *
 * **`--panel-hidden`, not a literal `-102%`.** `inset-inline-start` is logical
 * and lands on the right in RTL, while `translateX(-102%)` is PHYSICAL and
 * always moves left — mixing them slid a right-anchored panel INTO a Hebrew
 * viewport instead of off the edge. That is step 50, found by a person
 * looking at a screenshot. The token is flipped under `[dir="rtl"]` in
 * tokens.css so the markup stays direction-agnostic.
 */
export function ShellMenuToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const tr = useT();
  /*
   * One control, both jobs. It stays put and toggles — so the thing that
   * opened the menu is the thing that closes it, and it does not move or
   * vanish under the finger that just pressed it.
   */
  return (
    <button
      onClick={onToggle}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={open ? tr('Close the menu') : tr('Open the menu')}
      data-open={open || undefined}
      className="shell-toggle"
      
    >
      {/*
        * The app's own icon — the mark on its plate — instead of a hamburger
        * (the user, 2026-10-07). Drawn, not loaded from /brand, so it is there
        * offline and on the first frame. Open, the plate lights up in the
        * accent; the label says which way a press goes.
        */}
      <LogoMark size={26} />
    </button>
  );
}

/** What the drawer needs of a rave, whichever list it came from. */
interface DrawerRave {
  editionId: string;
  editionName: string;
  editionSlug?: string;
  festivalName: string;
  subdomain: string;
  /** The rave's logo — the submenu draws it before the edition (managed raves have none: the name). */
  logoUrl?: string | null;
  attending?: boolean;
  gatesOpenAt?: string | null;
  endsAt?: string | null;
}

function ShellDrawer({ open, onClose, extra }: {
  open: boolean; onClose: () => void;
  /** Publishes the rave-sections anchor, when the app is inside a rave. */
  extra: (name: ShellSlot, el: HTMLElement | null) => void;
}) {
  /*
   * `useLocation`, not `window.location.pathname`.
   *
   * The pathname read at render never changes without a full reload, so with
   * client-side routing the "you are here" mark would stick to whatever page
   * the app first loaded on. This re-renders when the route does.
   */
  const tr = useT();
  const here = useLocation().pathname;
  // Which host we are on decides what "my raves" means below.
  // Both admin hosts list the raves you ADMINISTER; everywhere else lists
  // the ones you are going to.
  const inAdmin = isRaveAdminHost() || isConsoleHost();
  const navigate = useNavigate();
  // Stable, for the same reason the shell's own anchor refs are: an inline
  // arrow is a new ref every render, and React answers a changed ref with a
  // `null` call and a node call — two state writes, and a render each.
  const menuRef = useCallback((el: HTMLDivElement | null) => extra('menuExtra', el), [extra]);

  /*
   * Is the rave list showing under "My raves"?
   *
   * Closed by default, everywhere. It was open on the dashboard, on the
   * reasoning that the list was already on screen there — which is the
   * argument AGAINST showing it: the menu repeated the page behind it, and
   * opened at its longest on the one screen that needed it least.
   */
  const [expanded, setExpanded] = useState(false);
  /*
   * A rave FRAMED in the setup wizard's preview (round 7, point 49): its menu
   * is the rave's own rows and nothing else — no My raves, Find a rave, Plans,
   * language or foot, nothing that leaves the rave or speaks for the platform.
   */
  const [framed] = useState(isFramed);
  const { scope: drawerScope } = useTenant();
  const raveOnly = framed && drawerScope === 'festival';

  /*
   * The person's raves, for the second level.
   *
   * Only asked once the drawer has actually been opened — most visits never
   * open it, and a menu is not worth a request on every page load. The query
   * key is the dashboard's, so the two share one cached answer rather than
   * fetching the same list twice.
   */
  /*
   * **Which raves, depends on where you are.**
   *
   * On the admin host "my raves" means the ones you MANAGE; everywhere else
   * it means the ones you are going to. Those are different lists — a
   * platform admin manages every festival and attends two — and showing the
   * attended list beside a dashboard of managed ones put two different counts
   * under one word on one screen.
   */
  /*
   * **The same key must mean the same SHAPE.**
   *
   * This first re-wrapped `/me/managed-raves` as `{ festivals }` under the
   * key `['me','managed-raves']` — which the admin dashboard also uses, with
   * the endpoint's real `{ raves }`. React Query caches by key, so whichever
   * ran first won and the other read a field that was not there: the drawer
   * quietly said "You are not attending anything yet" beside a grid of three
   * raves. Both callers now share the key AND the shape, and the drawer maps
   * at the point of use.
   */
  const mine = useQuery<{ raves?: DrawerRave[]; festivals?: DrawerRave[] }>({
    queryKey: inAdmin ? ['me', 'managed-raves'] : ['me', 'festivals'],
    queryFn: inAdmin
      ? () => api.get<{ raves: DrawerRave[] }>('/me/managed-raves')
      : () => endpoints.myFestivals(),
    enabled: open && !raveOnly,
  });

  /*
   * A–Z, by festival and then by edition.
   *
   * Sorting on the edition name alone would usually look the same — "OZORA
   * Festival 2026" and "…2027" land together — but only because the year is
   * at the end. A festival whose editions are named "OZORA Spring" and "OZORA
   * Winter" would interleave with another festival's. Festival first keeps
   * every edition of one rave in one block.
   */
  // My raves: the raves you attend that have not ended — the count AND the list (the user,
  // 2026-10-29: "my raves count 3 and shows 5"). The admin's list is every rave it manages.
  const listed: DrawerRave[] = inAdmin ? mine.data?.raves ?? [] : comingRaves(mine.data?.festivals ?? [], Date.now());
  const upcoming = inAdmin ? 0 : listed.length;

  const raves = [...listed].sort((a, b) =>
    a.festivalName.localeCompare(b.festivalName)
    || a.editionName.localeCompare(b.editionName));

  /*
   * Asked as soon as the drawer opens, whether or not the branch is expanded —
   * so the list is there the moment somebody taps the chevron instead of
   * flashing "one moment" at them.
   */

  useEffect(() => {
    if (!open) return;
    // An Escape something else used — the search closing its list or its results page — is not the menu's
    // (the user, 2026-11-02: "esc needs to close the search but don't close the menu"). On a computer the menu is a
    // column beside the page and Escape never closes it (the same day: "pressing esc closes the menu on the computer").
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented && !isWide()) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /*
   * Navigating does NOT close the menu.
   *
   * It used to call `onClose()` first — which writes `false` to storage — so
   * every time somebody used the menu, the menu put itself away and REMEMBERED
   * that. It looked like the state was not persisting; in fact it was
   * persisting a decision the person never made.
   *
   * It is a pinned column, not a modal: you navigate with it and it stays.
   */
  /*
   * Navigating to a PLATFORM page, from wherever the menu is being used.
   *
   * Inside a rave those pages are on another host — `app.` — which the router
   * cannot reach, so `navigate('/')` went to the rave's own root and the row
   * did nothing at all. Worse, the rave's root IS `/`, so "My raves" also
   * matched the current-page test and drew itself as the page you were
   * already on.
   *
   * So: on the platform host, a client-side navigation as before; inside a
   * rave, a real navigation to the other host, and `here` is not consulted
   * because none of these pages is the one you are on.
   */
  const { tenant, scope } = useTenant();
  const inRave = scope === 'festival';
  // The tier beside "Plans" — the same cached answer the account bubble reads.
  const { user: signedIn } = useAuth();
  const tier = planChip(useEntitlements(Boolean(signedIn)).data, tr);
  // No Plans while purchases are off — nothing could be bought (2026-10-21).
  const purchasesOn = usePurchasesOn() === true;
  /*
   * The same is true on `account.`, `radm.` and `appadm.`: none of them has
   * these routes (on `radm.` `/raves` was a blank page, on `appadm.` nothing
   * happened), so from anywhere but `app.` the row is a real navigation.
   */
  /*
   * On a phone the menu COVERS the page, so choosing a page closes it —
   * otherwise the new page arrives behind the menu (the user, 2026-10-07).
   * Not for a row that only opens or switches something in the menu
   * (`aria-expanded`, a switch, `data-keep-open`), and not from 880px, where
   * the menu is a column beside the page and stays as the person left it.
   *
   * A DOM listener, not React's `onClick`: the settings screens' section
   * lists are PORTALLED into the menu (`menuExtra`), and React bubbles a
   * portal's click to the screen that rendered it — never to this nav — so
   * picking "Notifications" on the account page left the menu open over it.
   */
  const drawerRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const nav = drawerRef.current;
    if (!nav) return undefined;
    const onClick = (e: MouseEvent) => {
      if (typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 879px)').matches) return;
      const hit = (e.target as Element | null)?.closest('button, a');
      if (!hit || !nav.contains(hit)) return;
      if (hit.hasAttribute('aria-expanded') || hit.getAttribute('role') === 'switch'
        || hit.hasAttribute('aria-checked') || hit.closest('[data-keep-open]')) return;
      closeRef.current();
    };
    nav.addEventListener('click', onClick);
    return () => nav.removeEventListener('click', onClick);
  }, []);

  const offApp = inRave || isAccountHost() || isRaveAdminHost() || isConsoleHost();
  const go = (path: string) => () => {
    if (offApp) void leaveTo(appUrl(path));
    else navigate(path);
  };
  const at = (match: (p: string) => boolean) => !offApp && match(here);

  return (
    <>
      {/* The scrim. Rendered only when open so it never eats a tap otherwise. */}
      {open && (
        <div
          className="shell-scrim"
          onClick={onClose}
          aria-hidden="true"
          /* Starts below the header, so the bar stays reachable with the
             menu open — the brand, the search and the account are all up
             there and none of them is what the scrim is dismissing. */
          
        />
      )}

      {/*
        * Always mounted, moved off-edge when closed, so it slides rather than
        * appearing — and so its contents are not rebuilt on every open.
        */}
      <nav
        ref={drawerRef}
        aria-label={tr('Menu')}
        aria-hidden={!open}
        className="shell-drawer" data-open={open || undefined}
      >
        {/*
          * No close control in here, and no lockup.
          *
          * The header's toggle is the way in and the way out — it stays
          * visible while the menu is open and does both. A second X six
          * pixels from it was one too many, and the header already carries
          * the brand.
          */}
        <Stack gap={2} className="shell-drawer-list">
          {/*
            * Navigation only, and only what is not already somewhere else.
            * "Sign out" lives under the person's own name; the privacy policy
            * and the version line are at the foot of this menu (below).
            */}

          {/*
            * A rave's own sections FIRST, then a rule, then the platform's.
            *
            * Inside a rave, the rave is what you are moving around in — its
            * schedule and its lineup are what the menu is being opened for,
            * and they were below two platform rows you would not have opened
            * it to reach. One menu, not two hamburgers, and the thing you are
            * in at the top of it.
            */}
          {/*
            * Whatever screen is open puts ITS OWN sections here, at the top.
            *
            * A rave puts its schedule and map; the account page puts profile,
            * subscription, password, devices. Same slot, because it is the
            * same idea: the menu lists where you can go, and where you can go
            * depends on where you are.
            *
            * Always rendered, never conditional on the host — the divider
            * hides itself when the slot is empty (`.shell-extra` in
            * tokens.css), which is one rule instead of every filler having to
            * tell the shell it exists.
            */}
          <div className="shell-extra">
            <div ref={menuRef} />
            {!raveOnly && <div className="shell-extra-rule shell-rule" />}
          </div>
          {!raveOnly && (<>

          {/*
            * "My raves" is TWO targets in one row: the label goes to the page,
            * the chevron expands the list underneath it.
            *
            * It used to be one button that only expanded, which left no way to
            * reach the page from the menu at all — and a second screen you had
            * to back out of to get anywhere else. Nested under the row, the
            * rest of the menu stays put while you read it.
            */}
          {/* Signed out there are no raves of yours (2026-10-26): the menu starts at Find a rave. */}
          {signedIn && <div className="shell-split" data-current={at((h) => h === myRavesPath()) || undefined}>
            <button
              role="menuitem"
              onClick={inAdmin ? () => navigate('/') : go(myRavesPath())}
              aria-current={at((h) => h === myRavesPath()) ? 'page' : undefined}
              className="shell-split-go"
            >
              {icon(MAIN_MENU_ICONS.heart)} {tr('My raves')}
              {upcoming > 0 && <span className="shell-menu-count">{upcoming > 99 ? '99+' : upcoming}</span>}
            </button>
            <button
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={expanded ? tr('Hide my raves') : tr('Show my raves')}
              data-open={expanded || undefined}
              className="shell-split-more"
              
            >
              <span className="shell-turn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
              </span>
            </button>
          </div>}

          {signedIn && expanded && (
            /*
              * Indented under the row it belongs to, with a rule down the
              * inline start edge — the thing that says "these are part of
              * that" without a heading repeating the word.
              */
            <div className="shell-sub">
              {mine.isLoading && (
                <div className="shell-sub-note">
                  {tr('One moment…')}
                </div>
              )}
              {!mine.isLoading && raves.length === 0 && (
                <div className="shell-sub-note">
                  {inAdmin ? tr('You do not manage any raves yet.') : tr('You are not attending anything yet.')}
                </div>
              )}
              {raves.map((r) => (
                <MenuItem
                  key={r.editionId}
                  /*
                    * Inside a rave, THIS is the row that is the page you are
                    * on. "My raves" above it is a different host and is not.
                    */
                  current={r.editionId === tenant?.editionId}
                  /*
                    * No `onClose()` here: on a wide screen the menu stays
                    * however the person left it, and the new page reads the
                    * state they chose. On a PHONE, where it covers the page,
                    * the drawer's own click handler closes it (2026-10-07).
                    */
                  onClick={() => void leaveTo(festivalUrl(r.subdomain, r.editionSlug))}
                >
                  <span className="shell-sub-dot" />
                  {/*
                    * The EDITION's name — "OZORA Festival 2026", not "OZORA".
                    * Somebody attending two years of the same rave would
                    * otherwise see the same line twice and have to guess.
                    */}
                  <span className="shell-sub-name">
                    <RaveMenuName festivalName={r.festivalName} editionName={r.editionName} logoUrl={r.logoUrl} />
                  </span>
                </MenuItem>
              ))}
            </div>
          )}

          {/* The first page, for everybody (2026-10-27): the rows of raves coming up. INSIDE a rave it
              is the way out, and says so (the user, 2026-10-27 signed out; 2026-10-30 for everybody). */}
          <MenuItem onClick={go('/')} current={at((h) => h === '/')}>
            <LogoMark size={18} /> {inRave ? tr('Return to upcoming raves') : tr('Upcoming raves')}
          </MenuItem>
          {/* Inside a rave the platform's menu is gone — My raves (and its list), the way back above and
              Support are all of it (the user, 2026-10-30: "keep only my raves and sub menu"; the 0.14.73 walk:
              "missing support in the rave menu"). */}
          {/* …and at the very end, under them: Invite a friend, then Support (the user, 2026-11-02: "in the rave menu,
              move support and invite a friend to the end of the menu, under my raves and return"). */}
          {inRave && <InviteFriendRow />}
          {inRave && (
            <MenuItem onClick={() => void leaveTo(supportUrl())}>
              {icon(MAIN_MENU_ICONS.support)} {tr('Support')}
            </MenuItem>
          )}
          {!inRave && (<>
          {/* All raves — every rave, with its filters (the user, 2026-10-29: "All raves", was "Find a
              rave") — needs an account: signed out, the menu's way back is Upcoming raves alone
              (the user, 2026-10-27: "signed out, back to the raves list returns me to sign in"). */}
          {signedIn && (
            <MenuItem onClick={go('/raves')} current={at((h) => h.startsWith('/raves'))}>
              {icon(MAIN_MENU_ICONS.find)} {tr('All raves')}
            </MenuItem>
          )}
          {/* Every artist on MyRave, by style (step 76) — for everybody; inside a rave the rave's
              own Artists row is the one (no two rows of one name). */}
          {!inRave && <MenuItem onClick={go('/artists')} current={at((h) => h === '/artists')}>
            {icon(MAIN_MENU_ICONS.artists)} {tr('Artists')}
          </MenuItem>}
          {/* Invite a friend (step 89) — inside a rave the rave's own row is the one. */}
          {!inRave && <InviteFriendRow />}
          {/* Support, in the menu too (the user, 2026-10-29). */}
          <MenuItem onClick={() => void leaveTo(supportUrl())}>
            {icon(MAIN_MENU_ICONS.support)} {tr('Support')}
          </MenuItem>

          {/*
            * Plans — what to buy, and what you have (the user, 2026-10-08:
            * "I need a page for subscription buy"). The tier rides beside it
            * once there is one; nothing for a free account.
            */}
          {purchasesOn && (
            <MenuItem onClick={go('/plans')} current={at((h) => h === '/plans')}>
              {icon(MAIN_MENU_ICONS.plans)} {tr('Plans')}
              {tier && <span className="shell-menu-tier">{tier}</span>}
            </MenuItem>
          )}

          {/* Messages are not here: they are an icon with a counter in the
              header, and a row in the account menu (the user, 2026-10-07). */}

          {/* Only when there is more than one language — see LanguageSwitch. */}
          <LanguageSwitch />
          </>)}
          </>)}

          {/*
            * No Sign out here: it lives ONLY in the account bubble's menu (the
            * user, 2026-09-28) — one place for it, where the account is.
            * "Your account" is not listed either: the bubble is the way in.
            */}

        </Stack>

        {/*
          * The foot of the menu: the privacy policy, then "© MyRave.io", which
          * opens the version matrix (the user, 2026-10-07) — on every host,
          * and the only place for them inside a rave, which has no footer.
          */}
        {!raveOnly && (
          <MenuFootLinks className="shell-drawer-foot" support={false} />
        )}
      </nav>
    </>
  );
}

/**
 * The footer's pages at the foot of a MENU — on a phone there is no footer
 * (the user, 2026-10-12: "we removed it on mobile and the pages moved to the
 * menu"): the rave's menu (`ShellDrawer`) and the consoles' sidebars
 * (`AdminShell`, `UserShell`) all end with these.
 */
export function MenuFootLinks({ className, support = true }: {
  className: string;
  /** False where the menu has its own Support row (2026-10-29) — the admins' sidebar keeps it here. */
  support?: boolean;
}) {
  const tr = useT();
  const { locale } = useLocale();
  return (
    <div className={className}>
      <a href={legalUrl('privacy', locale)} className="app-col-link">{tr('Privacy policy')}</a>
      <a href={legalUrl('terms', locale)} className="app-col-link">{tr('Terms of use')}</a>
      {support && <a href={supportUrl()} className="app-col-link">{tr('Support')}</a>}
      {/* A phone has no footer: MyRave's accounts are here, every menu's foot (2026-11-02). */}
      <FollowUs className="menu-follow-us" />
      <VersionMatrixButton newsHref={appUrl('/whats-new')} />
      {/* A phone has no footer here, so the people count is at the menu's foot too. */}
      <PeopleCount />
    </div>
  );
}

/** A place in the chrome a screen inside the shell can render into. */
export type ShellSlot = 'brandExtra' | 'centre' | 'controls' | 'trailing' | 'menuExtra' | 'crumb' | 'navFoot' | 'userRole';

/** The admins' frame (`admin/shell/AdminShell.tsx`) — lazy, in the admin chunk, never a visitor's download. */
const AdminShell = lazy(() => import('@/admin/shell/AdminShell'));
/** The user console's frame (`admin/shell/UserShell.tsx`) — `app.` and `account.` outside a rave. */
const UserShell = lazy(() => import('@/admin/shell/UserShell'));
const SLOT_NAMES: ShellSlot[] = ['brandExtra', 'centre', 'controls', 'trailing', 'menuExtra', 'crumb', 'navFoot', 'userRole'];

type SlotNodes = Partial<Record<ShellSlot, HTMLElement | null>>;

const SlotsCtx = createContext<SlotNodes>({});

/**
 * The chrome's empty anchor for `name`, to render into with a PORTAL.
 *
 * **Why a portal and not a prop.** The shell has to be on screen BEFORE the
 * thing that fills it exists: a rave's header needs the edition, which is a
 * fetch, and with the shell inside that fetch the whole page was a bare
 * "Loading the lineup…" with no header, no menu and no way out — then
 * everything at once. So the shell is mounted above the fetch.
 *
 * That leaves the filler outside the shell in the DOM but INSIDE it in the
 * React tree, which is exactly what a portal is: the rave's countdown and
 * brand still sit under `EditionProvider` and can read it, while appearing in
 * a header that rendered long before. Handing the shell finished elements
 * instead — the obvious version — puts them under the shell in the React tree
 * too, where `useEdition` is not in scope and every one of them throws.
 *
 * Null until the anchor mounts, and null forever on a host that has no such
 * slot. Guard, do not assert.
 */
export function useShellSlot(name: ShellSlot): HTMLElement | null {
  return useContext(SlotsCtx)[name] ?? null;
}

export function AppShell({ children }: { children: ReactNode }) {
  const tr = useT();
  const [menuOpen, setMenuOpen] = useMenuState();
  const [slots, setSlots] = useState<SlotNodes>({});
  const { scope } = useTenant();

  /*
   * One stable callback ref per slot. A ref built inline would be a new
   * function every render, which React answers by calling the old one with
   * `null` and the new one with the node — two state writes per render, and
   * the render they cause writes again.
   */
  const put = useCallback((name: ShellSlot, el: HTMLElement | null) => {
    setSlots((s) => (s[name] === el ? s : { ...s, [name]: el }));
  }, []);
  const brandRef = useCallback((el: HTMLElement | null) => put('brandExtra', el), [put]);
  const centreRef = useCallback((el: HTMLElement | null) => put('centre', el), [put]);
  const wideHeader = useWideHeader();
  const controlsRef = useCallback((el: HTMLElement | null) => put('controls', el), [put]);
  const trailingRef = useCallback((el: HTMLElement | null) => put('trailing', el), [put]);
  const crumbRef = useCallback((el: HTMLElement | null) => put('crumb', el), [put]);
  /** One stable callback per slot, for the admins' frame. */
  const adminPut = useMemo(
    () => Object.fromEntries(SLOT_NAMES.map((n) => [n, (el: HTMLElement | null) => put(n, el)])) as Record<ShellSlot, (el: HTMLElement | null) => void>,
    [put],
  );
  /*
   * The admin console's header says where you are (the user, 2026-10-09):
   * "MyRave.io | Admin Console | People". The page's name is the console's
   * to portal into the `crumb` slot — the shell does not know its sections.
   */
  const consoleHost = isConsoleHost();
  // The rave admin wears the console's header too (the user, 2026-10-09).
  const crumbHost = consoleHost ? tr('Admin Console') : isRaveAdminHost() ? tr('Rave Admin') : null;

  // Only a rave extends the header. On the platform's own pages the anchors
  // are not rendered at all, so the divider and the centre gap never appear.
  const inRave = scope === 'festival';

  // On a phone the header slides away while scrolling down (scrollChrome.tsx);
  // an open menu hangs off it, so it holds the header in place.
  useHeaderAway(menuOpen);

  /*
   * The admins wear their own frame — the Stitch design's sidebar and header
   * (the user, 2026-10-10: "make it exactly as I gave you").
   */
  if (crumbHost) {
    return (
      <SlotsCtx.Provider value={slots}>
        <NotificationOpened />
        <Suspense fallback={null}>
          <AdminShell kind={consoleHost ? 'console' : 'rave'} put={adminPut} onOpenAccount={() => void leaveTo(accountUrl('/'))}>
            {children}
          </AdminShell>
        </Suspense>
      </SlotsCtx.Provider>
    );
  }

  /*
   * The platform's own pages wear the user console's frame — the Stitch
   * design's (the user, 2026-10-10: "implement C the user console"; the
   * design's look on every platform page, never inside a rave).
   */
  if (!inRave && (isAppHost() || isAccountHost() || isSupportHost())) {
    return (
      <SlotsCtx.Provider value={slots}>
        <NotificationOpened />
        <Suspense fallback={null}>
          <UserShell put={adminPut} onOpenAccount={() => void leaveTo(accountUrl('/'))}>{children}</UserShell>
        </Suspense>
      </SlotsCtx.Provider>
    );
  }

  return (
    <SlotsCtx.Provider value={slots}>
    <div className="app-column">
      {/* What stays under the phone's status bar while the header is away. */}
      <div className="shell-statusbar" aria-hidden="true" />
      <PullToRefresh />
      <NativePushAuto />
      <ArrivalSync />
      {/* A push tap or an email button opened a notice: report it (step 61, §37.61). */}
      <NotificationOpened />
      <StoreSync />
      {/*
        * The toggle goes in the header's `menu` slot — the START edge, before
        * the brand — because that is the edge the drawer opens from.
        */}
      {/*
        * The header spans the whole width, like the footer — the menu opens
        * UNDER it, so it is the app's top edge rather than the content's.
        * Shifting it with the content left an empty rectangle above the
        * drawer and put the brand somewhere different depending on whether
        * the menu happened to be open.
        */}
      <AppHeader
        /* The words alone: the menu button IS the mark now (the user,
           2026-10-07), and two marks side by side read as a stutter. */
        brand={crumbHost ? (
          <span className="shell-crumbs">
            <a href={appUrl('/')} className="ui-lockup-link" aria-label={tr('MyRave.io home')}>
              <Wordmark size={19} />
            </a>
            <span aria-hidden="true" className="shell-crumb-rule" />
            <span className="shell-crumb">{crumbHost}</span>
            <span ref={crumbRef} className="shell-crumb-page" />
          </span>
        ) : (
          <a href={appUrl('/')} className="ui-lockup-link" aria-label={tr('MyRave.io home')}>
            <Wordmark size={19} />
          </a>
        )}
        menu={<ShellMenuToggle open={menuOpen} onToggle={() => setMenuOpen(!menuOpen)} />}
        brandExtra={inRave ? (
          <span
            ref={brandRef}
            className="hdr-tenant shell-slot is-tenant"
            /*
              * Capped, because a rave's wordmark is its own and can be any
              * width. OZORA's is five times as wide as it is tall and took
              * the account control off the edge of a 320px screen.
              */
            
          />
        ) : undefined}
        /* A computer: the search OPEN in the middle of the header, for everyone (the user, 2026-11-02: "on the web it can
           be in the middle of the header, open to everyone, no need to be closed"); a phone keeps the icon that opens. */
        centre={wideHeader || inRave
          ? <>{wideHeader && <HeaderSearch field />}{inRave && <span ref={centreRef} className="shell-slot" />}</>
          : undefined}
        /* Search first, then the rave's own controls. It is the platform's
           control and they are the page's, and a person reaches for search
           before anything else up here. */
        controls={<>{!wideHeader && <HeaderSearch />}{inRave && <span ref={controlsRef} className="shell-slot is-controls" />}<HeaderInbox /></>}
        trailing={inRave ? <span ref={trailingRef} className="shell-slot is-trailing" /> : undefined}
        account={
          <UserMenu
            /*
              * Cross-host when we are inside a rave: the account lives on
              * `app.`, which the router cannot reach from a festival
              * subdomain. Same rule the platform rows in the drawer follow.
              */
            /* Always a real navigation: the account is its own host now, so
               there is no in-app route to push. */
            onOpenAccount={() => void leaveTo(accountUrl('/'))}
          />
        }
      />

      <ShellDrawer open={menuOpen} onClose={() => setMenuOpen(false)} extra={put} />

      {/*
        * `data-menu` rather than an inline style: the shift only applies from
        * 880px, where the drawer is pinned instead of modal, and that is a
        * media query's job. See `.shell-body` in tokens.css.
        */}
      <div
        className="shell-body"
        data-menu={menuOpen ? 'open' : 'closed'}
      >
        {/* Below the header and above every screen, on every host that wears
            this shell — an admin's notice is about the platform, not a page. */}
        <MaintenanceBanner />
        {/* The phone app, signed out: sign in for the full app (2026-10-26). */}
        <SignInStrip />
        <SignInAsk />
        <main className="shell-main">{children}</main>
      </div>

      {/*
        * The footer sits OUTSIDE the shifted column, so it runs the whole
        * width of the app — across the menu as well as the content. It is the
        * app's bottom edge, not the content's, and a footer that stopped
        * where the drawer started would read as the page being cut short.
        *
        * It also draws ABOVE the drawer (see `.shell-footer` in tokens.css),
        * which is what lets a full-height drawer end at it.
        */}
      {/*
        * Only in the main app (the user, 2026-10-07). Inside a rave the page
        * is the rave's, to its last pixel; the privacy policy and the version
        * line are at the bottom of the menu instead.
        */}
      {/* Inside a rave too: on a computer the footer is always there (the user,
          2026-10-07); a phone's rave has none — its bar is at the bottom. */}
      <ShellFooter inRave={inRave} />

    </div>
    </SlotsCtx.Provider>
  );
}

/**
 * The drawer remembers whether it was open — at every width, on every page,
 * until somebody changes it.
 *
 * Platform-scoped (`myrave:platform:menuOpen`), through `lib/storage` rather than
 * `localStorage` (hard rule 6, and that helper survives a browser with site
 * data blocked or a private window, where `localStorage` throws rather than
 * returning null).
 *
 * It used to force closed below the pinning width, on the reasoning that a modal overlay
 * restoring itself on every load is hostile. That was solving the wrong
 * problem: the fix is that the drawer stopped being modal — it is a column
 * beside the content, the toggle is gone while it is open, and it closes from
 * its own X. So the stored answer is simply honoured.
 */
/*
 * Open or closed, remembered per kind of screen. On a WIDE screen the menu is
 * a column beside the page and starts OPEN (the user, 2026-10-07: "on full
 * web, make the menu always open, with the option to close it with the
 * toggle") — closing it is remembered. On a phone it covers the page and
 * starts closed. Two keys, so closing it on a phone never closes it on a
 * computer.
 */
const WIDE = '(min-width: 880px)';
const isWide = () => typeof window.matchMedia === 'function' && window.matchMedia(WIDE).matches;

function useMenuState(): [boolean, (v: boolean) => void] {
  const key = () => (isWide() ? MENU_KEY_WIDE : MENU_KEY);
  // A public page's preview is the screen alone — never the menu (`lib/preview.ts`).
  const [open, set] = useState<boolean>(() => !inPreview() && readJson<boolean>(null, key(), isWide()));
  // Festival mode's MORE (step 59) opens this drawer from inside a rave, below the shell.
  useEffect(() => {
    const on = (e: Event) => { const v = (e as CustomEvent<boolean>).detail; set(v); writeJson(null, key(), v); };
    window.addEventListener('myrave:menu', on);
    return () => window.removeEventListener('myrave:menu', on);
  }, []);
  // The window resized across the pinning width (the user, 2026-11-02): narrowed to a phone's width the menu closes by
  // itself, widened back to a computer's it opens by itself — neither is remembered as somebody's choice.
  useEffect(() => {
    if (inPreview() || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(WIDE);
    const on = () => set(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return [open, (v) => { set(v); writeJson(null, key(), v); }];
}

const MENU_KEY = 'menuOpen';
const MENU_KEY_WIDE = 'menuOpenWide';

/**
 * The app's slim footer: the brand at the start; at the end the version (©),
 * the privacy policy, the terms of use and support (the user, 2026-10-10).
 *
 * Deliberately not the public site's four-column footer. Inside the app a
 * footer is a boundary marker, not a site map — navigation is in the header
 * and the menu, and repeating it at the bottom of every screen is furniture
 * people scroll past.
 */
export function ShellFooter({ inRave = false }: { inRave?: boolean }) {
  const tr = useT();
  const { locale } = useLocale();
  return (
    <footer
      className={cx('shell-footer', inRave && 'is-rave')}
      data-print="hide"
    >
      <Wrap className="app-shell-footer-pad">
        <Row gap={14} className="shell-foot-row">
          {/* 16: the mark is one line of the fine print tall, and the
              wordmark (0.68×) sits at the text's own size. */}
          <BrandLockup size={16} href={appUrl('/')} />
          <Row gap={16} className="shell-foot-fine">
            <PeopleCount />
            <VersionMatrixButton newsHref={appUrl('/whats-new')} />
            <a href={legalUrl('privacy', locale)} className="app-col-link">{tr('Privacy policy')}</a>
            <a href={legalUrl('terms', locale)} className="app-col-link">{tr('Terms of use')}</a>
            <a href={supportUrl()} className="app-col-link">{tr('Support')}</a>
            <SocialIcons />
          </Row>
        </Row>
      </Wrap>
    </footer>
  );
}

/**
 * The phone's "you've arrived" places follow the raves you attend (step 60
 * task 4): once per load, signed in, in the app, when switched on — and only
 * when the list changed (`syncArrivals`).
 */
function ArrivalSync() {
  const { user } = useAuth();
  const uid = user?.id;
  useEffect(() => { if (uid) void syncArrivals(); }, [uid]);
  return null;
}
